import { FC, useEffect, useRef, useState } from 'react';
import cn from 'classnames';

import { DEFAULT_MEDICAL_IMAGE_HEIGHT } from '@/shared/config';
import {
  makeAnnotationsSvgNodes,
  type AnnotationPresentationVariant,
} from '@/shared/graphics/medicalImageRender/MedicalImageRender';

import type { Annotation } from '../AnnotationsLayer/Annotation';

import { renderAnnotationSvgNodes } from './lib';
import styles from './AnnotationsOverlay.module.scss';

type AnnotationsOverlayProps = {
  annotations: Annotation[];
  heightPx: number;
  widthPx: number;
  className?: string;
  scaleMmPerPixel?: number;
  variant?: AnnotationPresentationVariant;
};

export const AnnotationsOverlay: FC<AnnotationsOverlayProps> = (props) => {
  const {
    annotations,
    className,
    heightPx,
    scaleMmPerPixel,
    variant = 'thumbnail',
    widthPx,
  } = props;

  const svgRef = useRef<SVGSVGElement>(null);
  const [displayHeightPx, setDisplayHeightPx] = useState(0);
  const hasAnnotations = annotations.length > 0 && widthPx > 0 && heightPx > 0;

  useEffect(() => {
    const element = svgRef.current;

    if (!element || typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      setDisplayHeightPx(entries[0]?.contentRect.height ?? 0);
    });

    observer.observe(element);

    return () => observer.disconnect();
  }, [hasAnnotations]);

  if (!hasAnnotations) {
    return null;
  }

  const nodes = makeAnnotationsSvgNodes({
    annotations,
    displayHeightPx:
      displayHeightPx ||
      (variant === 'editor' ? heightPx : DEFAULT_MEDICAL_IMAGE_HEIGHT),
    heightPx,
    scaleMmPerPixel,
    variant,
    widthPx,
  });

  return (
    <svg
      ref={svgRef}
      data-component="annotations-overlay"
      className={cn(styles.overlay, className)}
      viewBox={`0 0 ${widthPx} ${heightPx}`}
      width="100%"
      height="100%"
      preserveAspectRatio="none"
    >
      {renderAnnotationSvgNodes(nodes)}
    </svg>
  );
};
